import React, { memo, useCallback, useEffect, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';

import colors from '@front/common/src/utils/colors';
import { toRem } from '@front/common/src/utils/styles';
import {
  AccordionProps as CommonAccordionProps,
  AccordionSubtitleVisibility,
} from '@front/common/src/types/components';
import { TOGGLE_ICON_SIZE } from '@front/common/src/components/accordion/constants';

import Text, { TextSize, textSizeMap } from '../base-text/variants/text';
import Icon from '../icon/icon';
import { WithClassNameProps } from '../types';

const MAIN_ICON_SIZE = 20;
const MAIN_ICON_MARGIN = 15;
export const CONTENT_PADDING = MAIN_ICON_SIZE + MAIN_ICON_MARGIN;
const ANIMATION_DURATION_SECONDS = 0.3;

const showAnimation = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;
const showAnimationStyle = css`
  display: block;
  animation: ${showAnimation} ${ANIMATION_DURATION_SECONDS}s ease-in-out;
`;

const hideAnimation = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;
const hideAnimationStyle = css`
  display: none;
  animation: ${hideAnimation} ${ANIMATION_DURATION_SECONDS}s ease-in-out;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const STYLE_BY_TITLE_POSITION: Record<
  NonNullable<AccordionProps['titlePosition']>,
  React.CSSProperties['justifyContent']
> = {
  center: 'center',
  left: 'flex-start',
  fullWidth: 'space-between',
};

const TitleContainer = styled.div<{
  $clickable: boolean;
  $titlePosition: NonNullable<AccordionProps['titlePosition']>;
}>`
  display: flex;
  align-items: center;
  width: 100%;
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
  justify-content: ${({ $titlePosition }) => STYLE_BY_TITLE_POSITION[$titlePosition]};
`;

const StyledMainIcon = styled(Icon)`
  margin-right: ${toRem(MAIN_ICON_MARGIN)};
`;

const StyledTitlePreview = styled(Text)<{
  $show: boolean;
}>`
  margin-right: ${toRem(15)};
  ${(props) => (props.$show ? showAnimationStyle : hideAnimationStyle)};
`;

const StyledRightSideTitleContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: ${toRem(10)};
`;

const OpenCloseIcon = styled(Icon)<{
  $isOpening: boolean;
}>`
  transition: ${ANIMATION_DURATION_SECONDS}s ease-in-out;
  transform: rotate(${(props) => (props.$isOpening ? '180deg' : '0deg')});
`;

type ContentProps = {
  $withIcon: boolean;
  $show: boolean;
};

const SubtitleContent = styled.div<ContentProps>`
  margin-left: ${({ $withIcon }) => ($withIcon ? toRem(CONTENT_PADDING) : 0)};
  ${({ $show }) => ($show ? showAnimationStyle : hideAnimationStyle)};
`;

const StyledContent = styled.div<ContentProps>`
  margin-top: ${toRem(10)};
  padding-left: ${({ $withIcon }) => ($withIcon ? toRem(CONTENT_PADDING) : 0)};
  ${(props) => (props.$show ? showAnimationStyle : hideAnimationStyle)};
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const showSubtitle = (visible: AccordionSubtitleVisibility, showContent: boolean): boolean => {
  if (visible === 'always') {
    return true;
  }

  if (visible === 'open') {
    return showContent;
  }

  return !showContent;
};

export type AccordionProps = CommonAccordionProps & {
  titleSize?: Extract<TextSize, 'MD' | 'MDMedium' | 'MDBold'>;
  testId?: string;
};

const Accordion = ({
  icon,
  title,
  titlePreview,
  subtitle,
  subtitleVisibility = 'always',
  isOpen = false,
  withToggle = true,
  children,
  className,
  titleSize = 'MDBold',
  titleColor = colors.ink100,
  iconLabel,
  iconColor = colors.primary100,
  hasContentPadding = true,
  titlePosition = 'fullWidth',
  testId,
}: AccordionProps & WithClassNameProps) => {
  const [isInternallyOpen, setIsInternallyOpen] = useState<boolean>(isOpen);
  const showContent = isInternallyOpen || !withToggle;

  const AccordionTitle = textSizeMap[titleSize];

  const doToggle = useCallback(() => {
    setIsInternallyOpen((previous) => !previous);
  }, []);

  useEffect(() => {
    setIsInternallyOpen(isOpen);
  }, [isOpen]);

  return (
    <Container className={className}>
      <TitleContainer
        data-testid={testId ? `${testId}-preview-title` : 'accordion-preview-title'}
        $titlePosition={titlePosition}
        $clickable={withToggle}
        onClick={withToggle ? doToggle : undefined}
        role='button'
      >
        <Row>
          {icon && (
            // Simple div that prevents the icon to change its size when the title is on 2 lines
            <div>
              <StyledMainIcon name={icon} size={MAIN_ICON_SIZE} color={iconColor} title={iconLabel} />
            </div>
          )}
          <AccordionTitle color={titleColor}>{title}</AccordionTitle>
        </Row>
        <StyledRightSideTitleContainer>
          <StyledTitlePreview $show={!!titlePreview && !showContent}>{titlePreview || ''}</StyledTitlePreview>
          {withToggle && (
            <OpenCloseIcon
              $isOpening={showContent}
              name='chevron-down'
              size={TOGGLE_ICON_SIZE}
              color={colors.ink100}
            />
          )}
        </StyledRightSideTitleContainer>
      </TitleContainer>
      {subtitle && (
        <SubtitleContent
          data-testid={testId ? `${testId}-preview-subtitle` : 'accordion-preview-subtitle'}
          $withIcon={!!icon}
          $show={showSubtitle(subtitleVisibility, showContent)}
        >
          {subtitle}
        </SubtitleContent>
      )}
      <StyledContent
        $show={showContent}
        $withIcon={!!icon && hasContentPadding}
        data-testid={testId ? `${testId}-content` : 'accordion-content'}
      >
        {children}
      </StyledContent>
    </Container>
  );
};

export default memo(Accordion);
